/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import AccountModel, { AccountState } from 'cio/models/account';
import BillingService from 'cio/services/billing';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import { computed } from '@ember/object';

const adminAlertContent = {
  noActiveSubscription: {
    title: 'Please set up your payment method',
    label: 'noActiveSubscription',
    route: 'settings.billing.checkout',
    content: ' to add a card and finish setting up your subscription.',
  },
  startDatePassed: {
    title: 'Please set up your payment method',
    label: 'startDatePassed',
    route: 'settings.billing.checkout',
    content:
      'Your subscription started on {pinnedStartDate} date. Click here to add a card and finish setting up your subscription.',
  },
  annualSub: {
    title: 'There is a problem with payment for your account',
    label: 'annualSub',
    route: 'settings.billing.billing-history',
    content: ' to see currently open invoices or contact ',
  },
  overagesOnly: {
    title: 'Your overages payment failed',
    label: 'overagesOnly',
    route: 'settings.billing.payment-methods',
    content: ' to retry or update the card on file or contact ',
  },
  notLumpSumOrOverages: {
    title: 'Your payment failed',
    label: 'notLumpSumOrOverages',
    route: 'settings.billing.payment-methods',
    content: ' to retry or update the card on file or contact ',
  },
};
const nonAdminAlertContent = {
  overdue: {
    title: 'There is a problem with payment for your account',
    label: 'overdue',
    content: 'Please ask your account admin to check the status of your payment or contact ',
  },
};

const defaultPaymentErrorAlert = {
  title: 'There is a problem with payment for your subscription',
  label: 'defaultPaymentErrorAlert',
  route: 'settings.billing.payment-methods',
  content: ' to retry or update the card on file or contact',
};

interface BillingPaymentIssueAlertArgs {
  currentAccount: AccountModel;
}

export default class BillingPaymentIssueAlert extends Component<BillingPaymentIssueAlertArgs> {
  @service billing!: BillingService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;

  @computed('billing.{billingInfo.lumpSum,subscription.hasAchSetup}')
  get isLumpSumSubscription() {
    return this.billing.billingInfo?.lumpSum === true || this.billing.subscription?.hasAchSetup === true;
  }

  @computed('billing.{billingInfo.lumpSum,subscription.hasFailedOverageCharge}')
  get isOveragesOnly() {
    return this.billing.billingInfo?.lumpSum === true && this.billing.subscription?.hasFailedOverageCharge === true;
  }

  @computed('billing.{billingInfo.activeSubscription,subscription.active}')
  get hasActiveSubscription() {
    return this.billing.billingInfo?.activeSubscription === true || this.billing.subscription?.active === true;
  }

  @computed('billing.billingInfo.pinnedStartDate')
  get hasPinnedStartDate() {
    return this.billing.billingInfo?.pinnedStartDate !== 0;
  }

  @computed('environment.account.current.accountState')
  get isAccountOverdue() {
    return this.environment.account?.current.accountState === AccountState.Overdue;
  }

  @computed('hasActiveSubscription', 'isLumpSumSubscription', 'billing.subscription.hasFailedOverageCharge')
  get showAnnualSubAlert() {
    return (
      this.hasActiveSubscription &&
      this.isLumpSumSubscription &&
      this.billing.subscription?.hasFailedOverageCharge !== true
    );
  }

  @computed('hasActiveSubscription', 'isOveragesOnly')
  get showOveragesOnlyAlert() {
    return this.hasActiveSubscription && this.isOveragesOnly;
  }

  @computed('hasActiveSubscription', 'isLumpSumSubscription', 'isOveragesOnly')
  get showNotLumpSumOrOveragesAlert() {
    return this.hasActiveSubscription && !this.isLumpSumSubscription && !this.isOveragesOnly;
  }

  @computed('hasPinnedStartDate', 'billing.startDateInFuture', 'hasActiveSubscription')
  get startDatePassedNoPaymentAlert() {
    return (
      this.hasPinnedStartDate &&
      !this.billing.startDateInFuture &&
      this.billing.billingInfo?.pinnedStartDate != null &&
      !this.hasActiveSubscription
    );
  }

  @computed('hasActiveSubscription', 'hasPinnedStartDate', 'billing.billingInfo.pinnedPlan')
  get noActiveSubscriptionAlert() {
    return !this.hasActiveSubscription && this.hasPinnedStartDate && this.billing.billingInfo?.pinnedPlan !== null;
  }

  @computed(
    'showAnnualSubAlert',
    'showOveragesOnlyAlert',
    'showNotLumpSumOrOveragesAlert',
    'startDatePassedNoPaymentAlert',
    'noActiveSubscriptionAlert',
    'billing.hasCompletedBillingInfoRequest',
  )
  get adminAlertToDisplay() {
    if (!this.auth.hasPermission('account/billing/view')) {
      return;
    }

    if (!this.billing.hasCompletedBillingInfoRequest) {
      return defaultPaymentErrorAlert;
    }

    switch (true) {
      case this.showAnnualSubAlert:
        return adminAlertContent.annualSub;
      case this.showOveragesOnlyAlert:
        return adminAlertContent.overagesOnly;
      case this.showNotLumpSumOrOveragesAlert:
        return adminAlertContent.notLumpSumOrOverages;
      case this.startDatePassedNoPaymentAlert:
        return adminAlertContent.startDatePassed;
      case this.noActiveSubscriptionAlert:
        return adminAlertContent.noActiveSubscription;
      default:
        return defaultPaymentErrorAlert;
    }
  }

  @computed('isAccountOverdue', 'auth.hasPermission')
  get nonAdminAlertToDisplay() {
    if (this.auth.hasPermission('account/billing/view')) {
      return;
    }
    if (!this.isAccountOverdue) {
      return;
    }
    return nonAdminAlertContent.overdue;
  }
}
